/**
 * Reading and writing markdown files without changing anything we were not asked
 * to change. Ported from the desktop's `main/fs/textfile.ts`, with Node's
 * Buffer replaced by Uint8Array and TextDecoder.
 *
 * A file has three properties beyond its text that a careless editor silently
 * rewrites: its encoding, whether it carries a byte-order mark, and whether its
 * lines end in LF or CRLF. Round-tripping those is the difference between a save
 * producing a one-line diff and a save producing a whole-file diff.
 */
import type { Encoding, Eol, TextFile } from './types'

export type { Encoding, Eol, TextFile }

const UTF8_BOM = [0xef, 0xbb, 0xbf]
const UTF16LE_BOM = [0xff, 0xfe]
const UTF16BE_BOM = [0xfe, 0xff]

function startsWith(bytes: Uint8Array, prefix: number[]): boolean {
  if (bytes.length < prefix.length) return false
  return prefix.every((b, i) => bytes[i] === b)
}

export function detectEncoding(bytes: Uint8Array): { encoding: Encoding; hasBom: boolean } {
  if (startsWith(bytes, UTF8_BOM)) return { encoding: 'utf8', hasBom: true }
  if (startsWith(bytes, UTF16LE_BOM)) return { encoding: 'utf16le', hasBom: true }
  if (startsWith(bytes, UTF16BE_BOM)) return { encoding: 'utf16be', hasBom: true }
  return { encoding: 'utf8', hasBom: false }
}

/** True on Windows, where a document with no line breaks yet gets CRLF. */
function windowsLike(): boolean {
  const platform =
    (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    ''
  return /win/i.test(platform)
}

/**
 * Picks the dominant line ending. Mixed files are common (a CRLF file edited on
 * Linux, say); we follow the majority so a save does not flip every other line.
 */
export function detectEol(content: string, fallback: Eol = windowsLike() ? '\r\n' : '\n'): Eol {
  const crlf = (content.match(/\r\n/g) ?? []).length
  const lf = (content.match(/(?<!\r)\n/g) ?? []).length
  if (crlf === 0 && lf === 0) return fallback
  return crlf >= lf ? '\r\n' : '\n'
}

function decode(bytes: Uint8Array, encoding: Encoding, hasBom: boolean): string {
  if (encoding === 'utf16be') {
    return new TextDecoder('utf-16be').decode(bytes.subarray(hasBom ? 2 : 0))
  }
  if (encoding === 'utf16le') {
    return new TextDecoder('utf-16le').decode(bytes.subarray(hasBom ? 2 : 0))
  }
  // ignoreBOM: the BOM, when there is one, is skipped here and recorded, never
  // silently swallowed by the decoder.
  return new TextDecoder('utf-8', { ignoreBOM: true }).decode(bytes.subarray(hasBom ? 3 : 0))
}

/** UTF-16 by hand: TextEncoder only writes UTF-8. */
function encodeUtf16(content: string, bigEndian: boolean): Uint8Array {
  const out = new Uint8Array(content.length * 2)
  for (let i = 0; i < content.length; i++) {
    const unit = content.charCodeAt(i)
    const hi = unit >> 8
    const lo = unit & 0xff
    out[i * 2] = bigEndian ? hi : lo
    out[i * 2 + 1] = bigEndian ? lo : hi
  }
  return out
}

function concat(prefix: number[], body: Uint8Array): Uint8Array {
  const out = new Uint8Array(prefix.length + body.length)
  out.set(prefix, 0)
  out.set(body, prefix.length)
  return out
}

export function encode(content: string, encoding: Encoding, hasBom: boolean): Uint8Array {
  if (encoding === 'utf16be') {
    const body = encodeUtf16(content, true)
    return hasBom ? concat(UTF16BE_BOM, body) : body
  }
  if (encoding === 'utf16le') {
    const body = encodeUtf16(content, false)
    return hasBom ? concat(UTF16LE_BOM, body) : body
  }
  const body = new TextEncoder().encode(content)
  return hasBom ? concat(UTF8_BOM, body) : body
}

/** Decodes raw bytes the way a file read would. */
export function decodeText(bytes: Uint8Array): TextFile {
  const { encoding, hasBom } = detectEncoding(bytes)
  const raw = decode(bytes, encoding, hasBom)
  return {
    content: raw.replace(/\r\n/g, '\n'),
    encoding,
    hasBom,
    eol: detectEol(raw),
  }
}

/** The bytes a save writes: the text with its own line endings, encoding and BOM. */
export function encodeText(
  content: string,
  format: Pick<TextFile, 'encoding' | 'hasBom' | 'eol'>
): Uint8Array {
  const withEol =
    format.eol === '\r\n' ? content.replace(/\r?\n/g, '\r\n') : content.replace(/\r\n/g, '\n')
  return encode(withEol, format.encoding, format.hasBom)
}

/**
 * A NUL byte in the first few KB means binary. Cheaper and more reliable than
 * trusting the extension.
 */
export function looksBinary(bytes: Uint8Array): boolean {
  const limit = Math.min(bytes.length, 4096)
  for (let i = 0; i < limit; i++) if (bytes[i] === 0) return true
  return false
}
